/**
 * OfferDetail - one student's availability, and asking them about a time
 * -----------------------------------------------------------------------
 * Nothing on this page books anybody. Choosing a time opens a short form -
 * what the job is, what it pays, where - and sends it to the student as a
 * question. They accept or decline, and only an acceptance creates a chore.
 *
 * The page says that in plain words next to the button, because a poster who
 * thinks they have booked a babysitter and then finds out they have not has a
 * genuine problem on their hands.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Send, AlertTriangle, MessageSquare } from 'lucide-react';

import { offerApi, messageApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { dateTime } from '../utils/format';
import { HALF_HOURS, atTime, horizonDate } from '../utils/schedule';
import DayPicker from '../components/DayPicker';
import Loader from '../components/Loader';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import BackLink from '../components/BackLink';
import ReportButton from '../components/ReportButton';

import { useI18n } from '../hooks/useI18n';

/**
 * The most times one request may carry.
 *
 * Matches MAX_SLOTS_PER_REQUEST in backend/routes/offers.js, which is what
 * actually enforces it. Written here as well so somebody is told before they
 * send rather than refused afterwards - and if the two ever disagree, the
 * server is the one that decides.
 */
const MAX_TIMES = 14;

/** '17:00' from a real moment, in the poster's own clock. */
const hhmm = (value) =>
  new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/**
 * THE STUDENT'S ORDINARY WEEK, READ BACK OUT OF THE DATES THE SERVER SENT.
 *
 * The screen wants "Mon 17:00-20:00", but what arrives is a fortnight of real
 * moments: the server projects the timetable forward and drops anything the
 * student is already booked for, then sends the RESULT. The GET /:id route in
 * backend/routes/offers.js deliberately does NOT send the raw weekly pattern
 * as well, on the grounds that it would be the same information twice.
 *
 * That is still the right call, and this is why it costs nothing. Grouping a
 * fortnight of occurrences by weekday gives back exactly the week the student
 * set - and it is BETTER than the raw timetable would have been, because an
 * evening they have since filled has already been taken out. Asking the server
 * for the pattern itself would have shown a poster a "usually free" Tuesday
 * the student is not free on.
 *
 * NO t() IN HERE, and it is not an oversight. This sits outside the component,
 * where there is no t to reach - the same shape of mistake as windowProblem()
 * in MyOffer.jsx, which took the whole availability screen down. Day names
 * come from the locale instead, which is how DayPicker.jsx does it and for the
 * reason written there: the browser already knows them in every language, so
 * seven more phrases per language would buy nothing.
 */
function weeklyPattern(slots, locale) {
  const found = new Map();

  (slots || []).forEach((slot) => {
    const day = new Date(slot.start).getDay();
    if (!found.has(day)) found.set(day, new Set());
    // A Set because the same window recurs every week in the fortnight, and
    // the point of this list is the pattern rather than each occurrence.
    found.get(day).add(`${hhmm(slot.start)}-${hhmm(slot.end)}`);
  });

  // Monday first: a school week, not a calendar month. getDay() puts Sunday at
  // 0, so the order is written out rather than sorted.
  return [1, 2, 3, 4, 5, 6, 0]
    .filter((day) => found.has(day))
    .map((day) => ({
      day,
      // 4 January 2021 was a Monday, so this lands on the right weekday name
      // whatever language the page is in.
      name: new Date(2021, 0, 4 + ((day + 6) % 7))
        .toLocaleDateString(locale, { weekday: 'short' }),
      times: [...found.get(day)].sort(),
    }));
}

export default function OfferDetail() {
  const { t, locale } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Separate from `error` above, which belongs to the page. The ask form is in
  // a modal, and a modal covers the page - so a refusal handed to the page
  // banner is handed to nobody. See the note in components/Modal.jsx.
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');

  /**
   * The times being asked about - one, or several at once.
   *
   * A poster who needs a dog walked on Monday, Wednesday and Friday used to
   * have to send three separate requests, which the student answered three
   * times over, producing three unrelated chores. Now the whole set travels
   * together and is answered once.
   *
   * Held as the slot objects rather than just their starts, so the dialog can
   * list the actual dates back before anything is sent.
   */
  /**
   * WHAT THE POSTER HAS CHOSEN.
   *
   * Days on our own calendar, one start time, and how long for. Several days
   * with one time is the shape a repeating job actually has - "walk the dog
   * at five on Monday, Wednesday and Friday" - and it travels as ONE request
   * the student answers once, so nobody ends up with two days out of three.
   *
   * Before 25 August 2026 this was a list of the student's own published
   * slots, and those were the only times that could be asked about.
   */
  const [days, setDays] = useState([]);
  const [time, setTime] = useState('17:00');
  const [hours, setHours] = useState(2);
  const [askOpen, setAskOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [pay, setPay] = useState('');
  const [address, setAddress] = useState('');
  const [sending, setSending] = useState(false);

  /**
   * ASKING A QUESTION FIRST - added 25 September 2026, at the owner's request.
   *
   * Separate state from the booking form above, because they are separate
   * things and conflating them is how somebody sends a booking request when
   * they meant to ask whether the student minds dogs.
   *
   * `conversation` comes from the server: whether this adult has already
   * asked, and whether they are waiting for an answer. The button says which.
   */
  const [conversation, setConversation] = useState({ started: false, awaitingReply: false });
  const [questionOpen, setQuestionOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [questionError, setQuestionError] = useState('');
  const [asking, setAsking] = useState(false);

  const toggleDay = (key) =>
    setDays((current) =>
      current.includes(key) ? current.filter((d) => d !== key) : [...current, key].sort()
    );

  const horizon = useMemo(() => horizonDate(3), []);

  /**
   * The chosen days turned into real moments, in the poster's own clock.
   *
   * `atTime` builds the date in the browser's timezone, which is the whole
   * reason the site does this here rather than on the server - "17:00" means
   * five in the afternoon where the person is standing, and getting it wrong
   * by an hour twice a year puts a sixteen-year-old at a door at the wrong
   * time. See utils/schedule.js.
   */
  const selected = useMemo(
    () =>
      days.map((key) => {
        const start = atTime(key, time);
        return {
          start: start.toISOString(),
          end: new Date(start.getTime() + (Number(hours) || 1) * 3600000).toISOString(),
        };
      }),
    [days, time, hours]
  );

  // The server refuses anything before 07:00 - see utils/availability.js.
  // Saying so here means somebody is not refused after filling the form in.
  const tooEarly = Number(String(time).slice(0, 2)) < 7;

  // Worked out here rather than where it is drawn, because hooks cannot run
  // after the "still loading" and "no such offer" returns below. `offer` is
  // null until the fetch lands, hence the optional chain.
  const pattern = useMemo(() => weeklyPattern(offer?.slots, locale), [offer, locale]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await offerApi.one(id);
      setOffer(data.offer);
      setConversation(data.conversation || { started: false, awaitingReply: false });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Sends the one question, then goes straight to the conversation.
   *
   * Going there rather than staying here is the honest thing to do: what
   * happens next is the student's answer, and it will arrive in the
   * conversation rather than on this page.
   */
  const askQuestion = async (event) => {
    event.preventDefault();
    const body = question.trim();
    if (!body) return;

    setAsking(true);
    setQuestionError('');
    try {
      await messageApi.offerSend(id, body);
      navigate(`/messages/offer/${id}`);
    } catch (err) {
      setQuestionError(err.message);
    } finally {
      setAsking(false);
    }
  };

  const send = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');
    try {
      // `starts` is the multi-slot form. `start` is still sent alongside it
      // for the single-slot case, which is the shape this route has always
      // accepted - see the request route in backend/routes/offers.js.
      await offerApi.request(id, {
        start: selected[0].start,
        starts: selected.map((s) => s.start),
        // How long for. The student's own window used to supply this, back
        // when the poster was picking one of their slots; now they choose the
        // start, so they have to say how long as well - otherwise the student
        // is agreeing to a job without knowing if it is one hour or eight.
        hours: Number(hours),
        title,
        message,
        pay: Number(pay),
        address,
      });
      const count = selected.length;
      setAskOpen(false);
      setDays([]);
      setTitle('');
      setMessage('');
      setPay('');
      setAddress('');
      setNotice(
        count === 1
          ? `Sent. ${offer.student.firstName} will see it and can accept or decline - you will be told either way.`
          : `Sent - all ${count} times, as one request. ${offer.student.firstName} sees every date and `
            + 'answers once, so you will not end up with half of what you asked for.'
      );
      await load();
    } catch (err) {
      // Into the modal, which is what is on screen. The refusals this route
      // gives - too early, clashes with work already agreed, blocked - are
      // exactly the ones somebody has to read to know what to change.
      setFormError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Loader label={t('offerDetail.loading')} />;

  if (!offer) {
    return (
      <div className="container section">
        <div className="alert alert-error">{error || t('offerDetail.notFound')}</div>
      </div>
    );
  }

  const student = offer.student;

  return (
    <div className="container section">
      <BackLink to="/offers" label={t('offerDetail.back')} />

      {notice && <div className="alert alert-success" style={{ margin: 'var(--space-sm) 0' }}>{notice}</div>}
      {error && <div className="alert alert-error" style={{ margin: 'var(--space-sm) 0' }}>{error}</div>}

      {/* ---------------- WHO, IN ONE LINE ----------------
          Cut back to a name and a school year on 25 August 2026, at the
          owner's request.

          There used to be a full profile card here: a large photograph, the
          star rating, the number of jobs done and how far they travel, all
          above the fold. It was a whole screen of a named child standing
          between an adult and the thing they came to do, and everything in it
          except the name had already been read on the card they tapped to get
          here.

          THE REPORT BUTTON STAYS, and stays at the top. This is the screen
          where an adult is deciding to ask a sixteen-year-old to come to their
          house, so the way to raise a concern belongs on it - the same
          reasoning that keeps Report pinned in the message thread header. It
          is smaller than it was; it has not moved out of reach. */}
      <div
        className="row-between"
        style={{ alignItems: 'baseline', flexWrap: 'wrap', gap: 'var(--space-xs)' }}
      >
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <h2 style={{ margin: 0 }}>
            {student.firstName} {student.lastName?.[0]}.
          </h2>
          <Badge tone="amber">{student.schoolYear}</Badge>
        </div>
        <ReportButton aboutUser={student._id} personName={student.firstName} />
      </div>

      <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 'var(--space-sm)' }}>
        {/* Label translated, stored value untouched - see ChoreCard and the
            warning at the top of i18n/en.js. */}
        {offer.categories.map((c) => (
          <Badge key={c} tone="soft-blue">{t(`categories.${c}`)}</Badge>
        ))}
      </div>

      <p style={{ marginTop: 'var(--space-sm)' }}>{offer.description}</p>

      {/* Where they will work, and how far. Kept when the rest of the profile
          went because it is about the OFFER rather than the person, and a
          poster who is outside the distance a student will travel should know
          before they write out a job. Both halves used to be English written
          into the page by hand. */}
      <div className="card-meta" style={{ marginBottom: 'var(--space-lg)' }}>
        <span>
          <MapPin size={14} aria-hidden="true" />
          {offer.area?.maxTravelKm
            ? t('offerDetail.worksInUpTo', { city: offer.area.city, km: offer.area.maxTravelKm })
            : t('offerDetail.worksInAnywhere', { city: offer.area?.city })}
        </span>
      </div>

      {/* ---------------- ASK SOMETHING FIRST ----------------
          Added 25 September 2026, at the owner's request: the same "ask about
          it" the chore page has, for the other direction.

          IT IS NOT THE SAME THING, THOUGH, and the wording here carries that.
          On a chore it is a student asking an adult. Here it is an adult
          writing to a sixteen-year-old, which is the one conversation on this
          site that does not begin with the child - so it is one question, the
          student decides whether to answer, and nothing more can be sent
          until they do. The text below says so rather than leaving somebody
          to find out when the box refuses them. */}
      <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
        <div className="row-between" style={{ gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 200, flex: 1 }}>
            <strong>{t('offerDetail.questionTitle')}</strong>
            <p className="text-sm text-muted" style={{ margin: '2px 0 0' }}>
              {conversation.awaitingReply
                ? t('offerDetail.questionWaiting', { name: student.firstName })
                : t('offerDetail.questionBody', { name: student.firstName })}
            </p>
          </div>

          {conversation.started ? (
            <Link to={`/messages/offer/${offer._id}`} className="btn btn-outline btn-inline">
              <MessageSquare size={16} aria-hidden="true" />
              {t('offerDetail.seeConversation')}
            </Link>
          ) : (
            <button
              type="button"
              className="btn btn-outline btn-inline"
              onClick={() => { setQuestion(''); setQuestionError(''); setQuestionOpen(true); }}
            >
              <MessageSquare size={16} aria-hidden="true" />
              {t('offerDetail.askAboutOffer')}
            </button>
          )}
        </div>
      </div>

      {/* ---------------- WHEN THEY SAY THEY ARE USUALLY FREE ----------------
          A closed dropdown since 25 August 2026, at the owner's request.

          It is advice, not a menu - until 25 August these were the only times
          that could be asked about, and now a poster picks whatever suits.
          Advice that is not being followed should not take up the top of the
          screen, so it folds away; somebody who wants to aim for a yes opens
          it, and everybody else goes straight to the form below.

          The heading and the days are the whole thing now. A paragraph
          underneath used to explain that this was a guide rather than a limit,
          which the form below already establishes by letting them pick
          anything. */}
      {pattern.length > 0 && (
        <details className="offer-week-toggle">
          <summary>
            <Clock size={16} className="text-green" aria-hidden="true" />
            {t('offerDetail.usuallyFree', { name: student.firstName })}
          </summary>

          <div className="offer-week-summary">
            {pattern.map(({ day, name, times }) => (
              <p className="offer-summary-line" key={day}>
                <span className="offer-summary-day">{name}</span>
                <span className="offer-summary-times">{times.join(', ')}</span>
              </p>
            ))}
          </div>
        </details>
      )}

      {/* ---------------- PICK WHEN YOU NEED THEM ----------------
          Our own calendar and a dropdown, never <input type="date"> or
          "time" - Chrome draws those in its own locale and puts the month
          first on an English machine. HANDOFF section 10b has the story. */}
      <div className="card">
        <h3>{t('offerDetail.whenDoYouNeedThem')}</h3>
        <p className="text-sm text-muted">{t('offerDetail.pickDaysHint', { name: student.firstName })}</p>

        <div className="field">
          <span className="label">{t('offerDetail.whichDays')}</span>
          <DayPicker selected={days} maxDate={horizon} onToggle={toggleDay} />
        </div>

        <div className="form-row">
          <div className="field">
            <label className="label" htmlFor="ask-time">{t('offerDetail.whatTime')}</label>
            <select id="ask-time" value={time} onChange={(e) => setTime(e.target.value)}>
              {HALF_HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="ask-hours">{t('offerDetail.howLong')}</label>
            <input
              id="ask-hours"
              type="number"
              min="0.5"
              max="12"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </div>
        </div>

        {/* Said before they send rather than as a red error afterwards. The
            server refuses these too - utils/availability.js - but somebody
            should not have to be refused to find out. */}
        {tooEarly && (
          <div className="alert alert-warning">
            <AlertTriangle size={18} aria-hidden="true" />
            <div className="text-sm">{t('offerDetail.tooEarly')}</div>
          </div>
        )}

        {selected.length > MAX_TIMES && (
          <div className="alert alert-warning">
            <AlertTriangle size={18} aria-hidden="true" />
            <div className="text-sm">{t('offerDetail.tooMany', { max: MAX_TIMES })}</div>
          </div>
        )}

        {selected.length > 0 && (
          <div className="schedule-summary" style={{ marginTop: 'var(--space-sm)' }}>
            <div className="schedule-summary-head">
              <Clock size={18} aria-hidden="true" />
              <strong>{t('offerDetail.timesChosen', { count: selected.length })}</strong>
            </div>
            <p className="schedule-summary-line">
              {selected.map((s) => dateTime(s.start)).join(' · ')}
            </p>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ marginTop: 'var(--space-xs)' }}
              disabled={tooEarly || selected.length > MAX_TIMES}
              onClick={() => {
                setAskOpen(true);
                setFormError('');
                setAddress(user?.location?.address || '');
              }}
            >
              <Send size={15} />{' '}
              {selected.length === 1
                ? t('offerDetail.askAboutOne')
                : t('offerDetail.askAboutMany', { count: selected.length })}
            </button>
          </div>
        )}
      </div>

      {/* ---------------- THE ASK ---------------- */}
      <Modal
        open={questionOpen}
        onClose={() => setQuestionOpen(false)}
        title={t('offerDetail.askAboutOffer')}
        error={questionError}
        actions={(
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setQuestionOpen(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="question-form" className="btn btn-primary btn-inline" disabled={asking}>
              {asking ? t('offerDetail.sendingQuestion') : t('offerDetail.sendQuestion')}
            </button>
          </>
        )}
      >
        <form id="question-form" onSubmit={askQuestion} className="stack">
          {/* The refusal is handed to the Modal above rather than drawn here:
              it puts an error where somebody scrolled to the bottom of a form
              on a phone will actually see it. See components/Modal.jsx. */}

          {/* Said before they write, not after they send. */}
          <p className="text-sm text-muted" style={{ margin: 0 }}>
            {t('offerDetail.questionRules', { name: student.firstName })}
          </p>

          <div className="field">
            <label htmlFor="offer-question">{t('offerDetail.yourQuestion')}</label>
            <textarea
              id="offer-question"
              rows={4}
              maxLength={2000}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('offerDetail.questionPlaceholder')}
            />
          </div>
        </form>
      </Modal>

      <Modal
        open={askOpen}
        onClose={() => setAskOpen(false)}
        error={formError}
        title={
          selected.length === 1
            ? t('offerDetail.askTitleOne', {
                name: student.firstName,
                when: dateTime(selected[0].start),
              })
            : t('offerDetail.askTitleMany', {
                name: student.firstName,
                count: selected.length,
              })
        }
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setAskOpen(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="ask-form" className="btn btn-primary btn-inline" disabled={sending}>
              {sending ? t('offerDetail.sending') : t('offerDetail.sendRequest')}
            </button>
          </>
        }
      >
        <form id="ask-form" onSubmit={send} className="stack" style={{ gap: 'var(--space-sm)' }}>
          {/* Every date written out inside the dialog. The poster is about to
              commit to a number, and the number depends on how many dates
              there are - so the dates have to be in front of them here, not
              only on the page behind. */}
          {selected.length > 1 && (
            <ul className="visit-list">
              {selected.map((slot) => (
                <li className="visit-row" key={slot.start}>
                  <span className="visit-when">{dateTime(slot.start)}</span>
                  <span className="visit-note">
                    until{' '}
                    {new Date(slot.end).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="field">
            <label htmlFor="ask-title">{t('offerDetail.whatIsJob')} *</label>
            <input
              id="ask-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder={t('offerDetail.jobPlaceholder')}
              required
            />
          </div>

          <div className="field">
            {/* Per visit, and the label has to say so. "€15" against six
                evenings means €90, and somebody who read it as the total
                would be five times out. */}
            <label htmlFor="ask-pay">
              {selected.length > 1 ? `${t('offerDetail.payEach')} *` : `${t('offerDetail.payOne')} *`}
            </label>
            <input
              id="ask-pay"
              type="number"
              min={1}
              max={1000}
              value={pay}
              onChange={(e) => setPay(e.target.value)}
              required
            />
            {selected.length > 1 && Number(pay) > 0 && (
              <span className="hint">
                €{Number(pay)} × {selected.length} times ={' '}
                <strong>€{Math.round(Number(pay) * selected.length * 100) / 100} in all</strong>
              </span>
            )}
            <span className="hint">
              {student.firstName} sees this before deciding. Cash goes directly between the two of
              you - ChoreConnect never handles it.
            </span>
          </div>

          <div className="field">
            {/* Required since 16 September 2026, as on a posted chore - a
                student who says yes needs somewhere to go. The server refuses
                a request without one; this says so before sending. */}
            <label htmlFor="ask-address">{t('offerDetail.address')} *</label>
            <input
              id="ask-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              autoComplete="street-address"
              minLength={3}
              maxLength={200}
              required
              placeholder={t('offerDetail.addressPlaceholder')}
            />
            <span className="hint">
              {t('offerDetail.addressHint')}
            </span>
          </div>

          <div className="field">
            <label htmlFor="ask-message">{t('offerDetail.anythingElse')}</label>
            <textarea
              id="ask-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
              placeholder={t('offerDetail.anythingPlaceholder')}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}
